// Chooses the race source: the live signal first, with the labelled recorded race as the fallback.
// - No live frame within 8 s of starting, or none for 20 s later on: switch to the replay.
// - The live socket keeps trying meanwhile. Once live frames flow again, switch back at the next
//   post time (a live starting frame), so a race is never cut in half.
// - `mode` forces one source and turns the fallback off.
import type { RaceSource, SignalFrame } from '../../race/types';
import { realScheduler, type Scheduler, type SourceStatus } from './types';

export const STARTUP_TIMEOUT_MS = 8000;
export const LOST_TIMEOUT_MS = 20000;
const WATCH_MS = 1000;

export type SourceKind = 'live' | 'replay';

export interface SwitcherCallbacks {
  /** A frame from the active source, with its arrival time on that source's clock. */
  onFrame(frame: SignalFrame, arrivalMs: number): void;
  /** The active source changed. Arrival times restart on the new source's clock; `rate` is replay speed. */
  onSourceChange(kind: SourceKind, label: string, rate: number): void;
  /** Connection status of the live socket (or of the replay when it is the only source). */
  onStatus?(status: SourceStatus): void;
}

export interface SwitcherOptions {
  mode: SourceKind | null; // null = live first, replay fallback
  live: () => RaceSource;
  replay: () => Promise<RaceSource & { speed?: number }>;
  scheduler?: Scheduler;
}

export class SourceSwitcher {
  private live: RaceSource | null = null;
  private replay: (RaceSource & { speed?: number }) | null = null;
  private replayLoading = false;
  private active: SourceKind | null = null;
  private startedAt = 0;
  private lastLiveFrame = -Infinity;
  private watch: unknown = null;
  private cb: SwitcherCallbacks | null = null;
  private readonly sched: Scheduler;

  constructor(private readonly opts: SwitcherOptions) {
    this.sched = opts.scheduler ?? realScheduler;
  }

  get activeKind(): SourceKind | null {
    return this.active;
  }

  start(cb: SwitcherCallbacks): void {
    this.cb = cb;
    this.startedAt = this.sched.now();
    if (this.opts.mode === 'replay') {
      void this.activateReplay();
      return;
    }
    this.live = this.opts.live();
    this.live.start((f, at) => this.onLiveFrame(f, at), (s) => this.cb?.onStatus?.(s));
    if (this.opts.mode === null) this.tick();
  }

  stop(): void {
    this.sched.clearTimeout(this.watch);
    this.live?.stop();
    this.replay?.stop();
    this.cb = null;
  }

  private onLiveFrame(f: SignalFrame, at: number): void {
    this.lastLiveFrame = this.sched.now();
    if (this.active === 'live') { this.cb?.onFrame(f, at); return; }
    // First live frame, or live is back while the replay plays: take over at post time only.
    if (this.active === null || f.state === 'starting') {
      this.replay?.stop();
      this.setActive('live', this.live!.label, 1);
      this.cb?.onFrame(f, at);
    }
  }

  private tick = (): void => {
    const now = this.sched.now();
    if (this.active === null && now - this.startedAt >= STARTUP_TIMEOUT_MS) void this.activateReplay();
    else if (this.active === 'live' && now - this.lastLiveFrame >= LOST_TIMEOUT_MS) void this.activateReplay();
    this.watch = this.sched.setTimeout(this.tick, WATCH_MS);
  };

  private async activateReplay(): Promise<void> {
    if (this.active === 'replay' || this.replayLoading) return;
    this.replayLoading = true;
    try {
      this.replay ??= await this.opts.replay();
    } catch (err) {
      console.warn('Could not load the recorded race:', err);
      if (this.opts.mode === 'replay') this.cb?.onStatus?.('failed');
      return;
    } finally {
      this.replayLoading = false;
    }
    // Live may have come back while the recording loaded.
    const active = this.active as SourceKind | null; // may have changed during the await
    if (!this.cb || active === 'replay') return;
    if (active === 'live' && this.sched.now() - this.lastLiveFrame < LOST_TIMEOUT_MS) return;
    const r = this.replay;
    this.setActive('replay', r.label, r.speed ?? 1);
    r.start((f, at) => { if (this.active === 'replay') this.cb?.onFrame(f, at); },
      (s) => { if (this.opts.mode === 'replay') this.cb?.onStatus?.(s); });
  }

  private setActive(kind: SourceKind, label: string, rate: number): void {
    this.active = kind;
    this.cb?.onSourceChange(kind, label, rate);
  }
}
